import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { PageHeader } from '../components/Navigation';
import { getBackPath } from '../utils/navigation';
import { apiFetch } from '../utils/api';
import './AnafInvoicesPage.css';

// Status badge component
const AnafStatusBadge = ({ status }) => {
  const statusMap = {
    processing: { className: 'status-processing', icon: '⏳', text: 'În procesare' },
    accepted: { className: 'status-accepted', icon: '✓', text: 'Acceptat' },
    rejected: { className: 'status-rejected', icon: '✗', text: 'Respins' }
  };
  const s = statusMap[status] || statusMap.processing;
  return (
    <span className={`anaf-status-badge ${s.className}`}>
      {s.icon} {s.text}
    </span>
  );
};

// Stats card component - clickable for filtering
const StatCard = ({ value, label, className, onClick, isActive }) => (
  <div
    className={`anaf-stat-card ${className || ''} ${isActive ? 'stat-active' : ''}`}
    onClick={onClick}
    style={{ cursor: 'pointer' }}
  >
    <div className="anaf-stat-value">{value}</div>
    <div className="anaf-stat-label">{label}</div>
  </div>
);

function AnafInvoicesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const backPath = getBackPath(location, '/validation-hub');

  // Dynamic back label and breadcrumbs based on where user came from
  const cameFromEmail = location.state?.from === 'email-viewer';
  const backLabel = cameFromEmail ? 'Înapoi la Email' : 'Înapoi la Validare';
  const breadcrumbs = cameFromEmail
    ? [
        { label: 'Validare', to: '/validation-hub' },
        { label: 'Email', to: backPath },
        { label: 'ANAF Facturi', to: null }
      ]
    : [
        { label: 'Validare', to: '/validation-hub' },
        { label: 'ANAF Facturi', to: null }
      ];

  const [invoices, setInvoices] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total_pages: 1, total: 0 });
  const [stats, setStats] = useState({ total_submitted: 0, processing: 0, accepted: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    dateFrom: '',
    dateTo: '',
    search: ''
  });
  const [page, setPage] = useState(1);
  const [actionLoading, setActionLoading] = useState({});

  // Error modal state for "Motiv" button
  const [errorModal, setErrorModal] = useState({
    open: false,
    invoiceId: null,
    invoiceNumber: null,
    errors: [],
    loading: false,
    error: null
  });

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: '20'
      });
      if (filters.status) params.append('status', filters.status);
      if (filters.dateFrom) params.append('date_from', filters.dateFrom);
      if (filters.dateTo) params.append('date_to', filters.dateTo);
      if (filters.search) params.append('search', filters.search);

      const response = await apiFetch(`/api/anaf/invoices?${params}`);
      const data = await response.json();

      if (data.success) {
        setInvoices(data.data.invoices);
        setPagination(data.data.pagination);
        setStats(data.data.stats);
      } else {
        setError(data.error || 'Eroare la încărcarea facturilor');
      }
    } catch (err) {
      setError('Eroare de conexiune: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  // Auto-check status for all 'uploaded'/'processing' invoices on page load
  const autoCheckPendingInvoices = useCallback(async (invoicesList) => {
    // Check both 'uploaded' (just sent) and 'processing' (ANAF still working)
    const pendingInvoices = invoicesList.filter(inv =>
      inv.anaf_status === 'uploaded' || inv.anaf_status === 'processing'
    );

    if (pendingInvoices.length === 0) return;

    console.log(`🔄 Auto-checking ${pendingInvoices.length} pending invoices...`);

    // Check status for each pending invoice (sequentially to avoid rate limits)
    for (const inv of pendingInvoices) {
      console.log(`📤 Auto-check: invoice ${inv.id} (status: ${inv.anaf_status})`);
      try {
        setActionLoading(prev => ({ ...prev, [inv.id]: 'status' }));
        const response = await apiFetch(`/api/anaf/invoices/status/${inv.id}`);
        const result = await response.json();
        console.log(`📥 Result for invoice ${inv.id}:`, result);
      } catch (err) {
        console.error(`❌ Error checking invoice ${inv.id}:`, err);
        // Silently fail - we'll show the current status
      } finally {
        setActionLoading(prev => ({ ...prev, [inv.id]: null }));
      }
    }

    // Refresh the list after all checks complete
    fetchInvoices();
  }, [fetchInvoices]);

  // Trigger auto-check when invoices are loaded (only on initial load)
  const [hasAutoChecked, setHasAutoChecked] = useState(false);

  useEffect(() => {
    if (!loading && invoices.length > 0 && !hasAutoChecked) {
      setHasAutoChecked(true);
      autoCheckPendingInvoices(invoices);
    }
  }, [loading, invoices, hasAutoChecked, autoCheckPendingInvoices]);

  const handleCheckStatus = async (invoiceId) => {
    setActionLoading(prev => ({ ...prev, [invoiceId]: 'status' }));
    try {
      const response = await apiFetch(`/api/anaf/invoices/status/${invoiceId}`);
      const data = await response.json();
      if (data.success) {
        fetchInvoices(); // Refresh list
      } else {
        alert(`Eroare: ${data.error}`);
      }
    } catch (err) {
      alert(`Eroare: ${err.message}`);
    } finally {
      setActionLoading(prev => ({ ...prev, [invoiceId]: null }));
    }
  };

  const handleDownload = async (invoiceId) => {
    setActionLoading(prev => ({ ...prev, [invoiceId]: 'download' }));
    try {
      const response = await apiFetch(`/api/anaf/invoices/download/${invoiceId}`);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `anaf_response_${invoiceId}.zip`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } else {
        const data = await response.json();
        alert(`Eroare: ${data.error}`);
      }
    } catch (err) {
      alert(`Eroare: ${err.message}`);
    } finally {
      setActionLoading(prev => ({ ...prev, [invoiceId]: null }));
    }
  };

  const handleGeneratePDF = async (invoiceId, numarFactura) => {
    setActionLoading(prev => ({ ...prev, [invoiceId]: 'pdf' }));
    try {
      const response = await apiFetch(`/api/anaf/invoices/${invoiceId}/pdf`);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `factura_${numarFactura}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } else {
        const data = await response.json();
        alert(`Eroare: ${data.error}`);
      }
    } catch (err) {
      alert(`Eroare: ${err.message}`);
    } finally {
      setActionLoading(prev => ({ ...prev, [invoiceId]: null }));
    }
  };

  const handleGeneratePDFv2 = async (invoiceId, numarFactura) => {
    setActionLoading(prev => ({ ...prev, [invoiceId]: 'pdfv2' }));
    try {
      const response = await apiFetch(`/api/anaf/invoices/${invoiceId}/pdf-anaf`);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `factura_anaf_${numarFactura}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } else {
        const data = await response.json();
        alert(`Eroare: ${data.error}`);
      }
    } catch (err) {
      alert(`Eroare: ${err.message}`);
    } finally {
      setActionLoading(prev => ({ ...prev, [invoiceId]: null }));
    }
  };

  const handleRetry = async (invoiceId) => {
    setActionLoading(prev => ({ ...prev, [invoiceId]: 'retry' }));
    try {
      const response = await apiFetch(`/api/anaf/invoices/retry/${invoiceId}`, {
        method: 'POST'
      });
      const data = await response.json();
      if (data.success) {
        // Navigate to invoice detail page for editing
        navigate(`/invoices/${invoiceId}`);
      } else {
        alert(`Eroare: ${data.error}`);
      }
    } catch (err) {
      alert(`Eroare: ${err.message}`);
    } finally {
      setActionLoading(prev => ({ ...prev, [invoiceId]: null }));
    }
  };

  // Handler for showing ANAF error details
  const handleShowErrors = async (invoiceId, invoiceNumber) => {
    setErrorModal({
      open: true,
      invoiceId,
      invoiceNumber,
      errors: [],
      loading: true,
      error: null
    });
    try {
      const response = await apiFetch(`/api/anaf/invoices/${invoiceId}/errors`);
      const data = await response.json();
      if (data.success) {
        setErrorModal(prev => ({
          ...prev,
          errors: data.errors || [],
          loading: false
        }));
      } else {
        setErrorModal(prev => ({
          ...prev,
          loading: false,
          error: data.error || 'Nu s-au putut încărca detaliile erorii'
        }));
      }
    } catch (err) {
      setErrorModal(prev => ({
        ...prev,
        loading: false,
        error: `Eroare de conexiune: ${err.message}`
      }));
    }
  };

  const closeErrorModal = () => {
    setErrorModal({
      open: false,
      invoiceId: null,
      invoiceNumber: null,
      errors: [],
      loading: false,
      error: null
    });
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleApplyFilters = () => {
    setPage(1);
    fetchInvoices();
  };

  const handleClearFilters = () => {
    setFilters({ status: '', dateFrom: '', dateTo: '', search: '' });
    setPage(1);
  };

  // Refresh button handler - fetches invoices AND triggers status check for processing ones
  const handleRefresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: '20'
      });
      if (filters.status) params.append('status', filters.status);
      if (filters.dateFrom) params.append('date_from', filters.dateFrom);
      if (filters.dateTo) params.append('date_to', filters.dateTo);
      if (filters.search) params.append('search', filters.search);

      const response = await apiFetch(`/api/anaf/invoices?${params}`);
      const data = await response.json();

      if (data.success) {
        setInvoices(data.data.invoices);
        setPagination(data.data.pagination);
        setStats(data.data.stats);

        // After fetching, trigger status check for uploaded/processing invoices
        // - 'uploaded': just sent to ANAF, needs first status check
        // - 'processing': ANAF still processing, needs re-check
        const pendingInvoices = data.data.invoices.filter(inv =>
          inv.anaf_status === 'uploaded' || inv.anaf_status === 'processing'
        );
        console.log(`🔍 Found ${pendingInvoices.length} pending invoices to check:`, pendingInvoices.map(i => ({id: i.id, status: i.anaf_status})));
        if (pendingInvoices.length > 0) {
          setLoading(false); // Allow UI to show while checking

          // Check status for each pending invoice
          for (const inv of pendingInvoices) {
            console.log(`📤 Checking ANAF status for invoice ${inv.id} (current: ${inv.anaf_status})`);

            try {
              setActionLoading(prev => ({ ...prev, [inv.id]: 'status' }));
              const statusResponse = await apiFetch(`/api/anaf/invoices/status/${inv.id}`);
              await statusResponse.json();
            } catch (err) {
              // Silently fail
            } finally {
              setActionLoading(prev => ({ ...prev, [inv.id]: null }));
            }
          }

          // Refresh the list after all checks
          fetchInvoices();
        }
      } else {
        setError(data.error || 'Eroare la încărcarea facturilor');
      }
    } catch (err) {
      setError('Eroare de conexiune: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('ro-RO');
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('ro-RO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="anaf-invoices-page">
      <PageHeader
        backTo={backPath}
        backLabel={backLabel}
        title="ANAF Facturi"
        subtitle="Urmărirea facturilor trimise către ANAF"
        breadcrumbs={breadcrumbs}
      />

      {/* Main content - constrained to 1400px like ValidationHub */}
      <div className="anaf-main-content">
        {/* Stats Cards - clickable for status filtering */}
        <div className="anaf-stats-cards">
        <StatCard
          value={stats.total_submitted}
          label="Total Trimise"
          isActive={filters.status === ''}
          onClick={() => { handleFilterChange('status', ''); setPage(1); }}
        />
        <StatCard
          value={stats.processing}
          label="În Procesare"
          className="stat-processing"
          isActive={filters.status === 'processing'}
          onClick={() => { handleFilterChange('status', 'processing'); setPage(1); }}
        />
        <StatCard
          value={stats.accepted}
          label="Acceptate"
          className="stat-accepted"
          isActive={filters.status === 'accepted'}
          onClick={() => { handleFilterChange('status', 'accepted'); setPage(1); }}
        />
        <StatCard
          value={stats.rejected}
          label="Respinse"
          className="stat-rejected"
          isActive={filters.status === 'rejected'}
          onClick={() => { handleFilterChange('status', 'rejected'); setPage(1); }}
        />
      </div>

      {/* Filters - simplified */}
      <div className="anaf-filters-bar">
        <input
          type="text"
          placeholder="Caută nr. factură..."
          value={filters.search}
          onChange={(e) => handleFilterChange('search', e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleApplyFilters()}
          className="anaf-search-input"
        />
        <button className="anaf-btn-refresh" onClick={handleRefresh} title="Reîncarcă și verifică status">
          <span className="anaf-btn-refresh-icon">↻</span>
          <span className="anaf-btn-refresh-text">Actualizează Status</span>
        </button>
      </div>

      {/* Error message */}
      {error && (
        <div className="anaf-error-message">
          {error}
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="anaf-loading">Se încarcă...</div>
      ) : invoices.length === 0 ? (
        <div className="anaf-empty-state">
          <p>Nu există facturi trimise către ANAF.</p>
        </div>
      ) : (
        <div className="anaf-table-container">
          <table className="anaf-invoices-table">
            <thead>
              <tr>
                <th>Nr. Factură</th>
                <th>Data</th>
                <th>Furnizor</th>
                <th>Beneficiar</th>
                <th>ID Încărcare</th>
                <th>Trimis la</th>
                <th>Status</th>
                <th>Răspuns ANAF</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map(inv => (
                <tr key={inv.id}>
                  <td>
                    <Link to={`/invoices/${inv.id}`} className="anaf-invoice-link">
                      {inv.numar_factura}
                    </Link>
                  </td>
                  <td>{formatDate(inv.data_factura)}</td>
                  <td className="anaf-cell-truncate" title={inv.client_name}>{inv.client_name || '-'}</td>
                  <td className="anaf-cell-truncate" title={inv.end_user_name}>{inv.end_user_name || '-'}</td>
                  <td className="anaf-cell-upload-id">{inv.anaf_upload_id}</td>
                  <td>{formatDateTime(inv.anaf_submitted_at)}</td>
                  <td><AnafStatusBadge status={inv.anaf_status} /></td>
                  <td className="anaf-cell-actions">
                    {/* Download ZIP - for accepted/rejected with download_id */}
                    {['accepted', 'rejected'].includes(inv.anaf_status) && inv.anaf_download_id && (
                      <button
                        className="anaf-btn-action anaf-btn-download"
                        onClick={() => handleDownload(inv.id)}
                        disabled={actionLoading[inv.id]}
                        title="Descarcă răspuns ANAF (ZIP)"
                      >
                        {actionLoading[inv.id] === 'download' ? '...' : 'Descarcă'}
                      </button>
                    )}

                    {/* Generate PDF buttons - only for accepted */}
                    {inv.anaf_status === 'accepted' && (
                      <button
                        className="anaf-btn-action anaf-btn-pdf"
                        onClick={() => handleGeneratePDF(inv.id, inv.numar_factura)}
                        disabled={actionLoading[inv.id]}
                        title="Generează PDF factură (simplu)"
                      >
                        {actionLoading[inv.id] === 'pdf' ? '...' : 'PDF'}
                      </button>
                    )}
                    {inv.anaf_status === 'accepted' && (
                      <button
                        className="anaf-btn-action anaf-btn-pdf"
                        onClick={() => handleGeneratePDFv2(inv.id, inv.numar_factura)}
                        disabled={actionLoading[inv.id]}
                        title="Generează PDF format ANAF oficial"
                      >
                        {actionLoading[inv.id] === 'pdfv2' ? '...' : (<>PDF<br/><span style={{fontSize: '0.7em'}}>v2</span></>)}
                      </button>
                    )}

                    {/* Motiv button - show error details for rejected invoices with download_id */}
                    {inv.anaf_status === 'rejected' && inv.anaf_download_id && (
                      <button
                        className="anaf-btn-action anaf-btn-motiv"
                        onClick={() => handleShowErrors(inv.id, inv.numar_factura)}
                        disabled={actionLoading[inv.id]}
                        title="Vezi motivul respingerii"
                      >
                        Motiv
                      </button>
                    )}

                    {/* Retry button - only for rejected invoices (after other buttons) */}
                    {inv.anaf_status === 'rejected' && (
                      <button
                        className="anaf-btn-action anaf-btn-retry"
                        onClick={() => handleRetry(inv.id)}
                        disabled={actionLoading[inv.id]}
                        title="Resetează și reîncearcă trimiterea"
                      >
                        {actionLoading[inv.id] === 'retry' ? '...' : 'Reîncearcă'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <div className="anaf-pagination">
            <button
              disabled={page <= 1}
              onClick={() => setPage(p => p - 1)}
            >
              ← Anterior
            </button>
            <span>Pagina {pagination.page} din {pagination.total_pages} ({pagination.total} rezultate)</span>
            <button
              disabled={page >= pagination.total_pages}
              onClick={() => setPage(p => p + 1)}
            >
              Următor →
            </button>
          </div>
        )}
      </div>

      {/* Error Modal for showing ANAF rejection reason */}
      {errorModal.open && (
        <div className="anaf-error-modal-overlay" onClick={closeErrorModal}>
          <div className="anaf-error-modal" onClick={(e) => e.stopPropagation()}>
            <div className="anaf-error-modal-header">
              <h3>Motiv respingere ANAF</h3>
              {errorModal.invoiceNumber && (
                <span className="anaf-error-modal-invoice">Factura: {errorModal.invoiceNumber}</span>
              )}
              <button className="anaf-error-modal-close" onClick={closeErrorModal} title="Închide">
                ×
              </button>
            </div>
            <div className="anaf-error-modal-content">
              {errorModal.loading ? (
                <div className="anaf-error-modal-loading">Se încarcă...</div>
              ) : errorModal.error ? (
                <div className="anaf-error-modal-error">{errorModal.error}</div>
              ) : errorModal.errors.length === 0 ? (
                <div className="anaf-error-modal-empty">Nu s-au găsit erori în răspunsul ANAF.</div>
              ) : (
                <ul className="anaf-error-list">
                  {errorModal.errors.map((err, i) => (
                    <li key={i} className="anaf-error-item">{err}</li>
                  ))}
                </ul>
              )}
            </div>
            <div className="anaf-error-modal-footer">
              <button className="anaf-btn-action" onClick={closeErrorModal}>Închide</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AnafInvoicesPage;
